import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixRestaurantsMenusV1ItemModifier from './restaurants-menus-v1-item-modifier-item-modifiers.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * An item modifier is a type of menu item that serves as an addition to a menu item.
 * Read more about [item modifiers](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item-modifiers/introduction).
 */
export interface Modifier {
  /**
   * Item modifier ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the item modifier is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the item modifier. <br />
   *
   * Ignored when creating an item modifier.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the item modifier was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the item modifier was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Item modifier display name.
   * @minLength 1
   * @maxLength 200
   */
  name?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /**
   * Whether the modifier is in stock.
   * Default: `true`.
   */
  inStock?: boolean | null;
  /**
   * IDs of the business locations ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where these item modifiers are available.
   * @maxSize 100
   * @format GUID
   * @readonly
   */
  businessLocationIds?: string[];
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface ExternalReferenceInfo {
  /**
   * ID of the external integrator app\system associated with this object.
   * @format GUID
   */
  externalAppId?: string | null;
  /**
   * Reference to an external resource ID. Used to link this object to a specific entity in an external system.
   * @maxLength 100
   */
  externalEntityId?: string | null;
  /** Last time this item modifier was synced with the external app. */
  lastSyncDate?: Date | null;
  /** Whether the connection to the external system is currently active. */
  connected?: boolean | null;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface CreateModifierRequest {
  /** Item modifier to create. */
  modifier: Modifier;
}

export interface CreateModifierResponse {
  /** Created item modifier. */
  modifier?: Modifier;
}

export interface GetModifierRequest {
  /**
   * ID of the item modifier to retrieve.
   * @format GUID
   */
  modifierId: string;
}

export interface GetModifierResponse {
  /** Retrieved item modifier. */
  modifier?: Modifier;
}

export interface ListModifiersRequest {
  /**
   * IDs of the item modifiers to retrieve.
   * @format GUID
   * @maxSize 500
   */
  modifierIds?: string[];
  /** Metadata of the paginated results. */
  paging?: CursorPaging;
}

export interface CursorPaging {
  /**
   * Maximum number of items to load.
   * @max 500
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface ListModifiersResponse {
  /** Retrieved item modifiers. */
  modifiers?: Modifier[];
  /** Metadata of the paginated results. */
  metadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor pointing to the next page and the previous page in the list of results. */
  cursors?: Cursors;
  /**
   * Whether there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to previous page in the list of results. */
  prev?: string | null;
}

export interface UpdateModifierRequest {
  /** Item Modifier with updated properties. */
  modifier: Modifier;
}

export interface UpdateModifierResponse {
  /** Updated item modifier. */
  modifier?: Modifier;
}

export interface BulkCreateModifiersRequest {
  /**
   * List of item modifiers to create.
   * @maxSize 100
   */
  modifiers: Modifier[];
  /**
   * Whether the created item modifiers are included in the response. <br />
   * Default: `false`.
   */
  returnEntity?: boolean;
}

export interface BulkCreateModifiersResponse {
  /** Information about the created item modifiers. */
  results?: BulkCreateModifierResult[];
  /** Metadata for Bulk Create Modifiers API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCreateModifierResult {
  /** Metadata for creation of the item modifer. */
  itemMetadata?: ItemMetadata;
  /** Created item modifier. */
  modifier?: Modifier;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateModifiersRequest {
  /**
   * List of item modifiers to update.
   * @minSize 1
   * @maxSize 100
   */
  modifiers: MaskedModifier[];
  /** Whether the updated item modifiers are included in the response. */
  returnEntity?: boolean;
}

export interface MaskedModifier {
  /** Item modifier to update. */
  modifier?: Modifier;
  /** Explicit list of fields to update. */
  mask?: string[];
}

export interface BulkUpdateModifiersResponse {
  /**
   * Information about the updated item modifiers.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateModifierResult[];
  /** Metadata for Bulk Update Modifiers API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateModifierResult {
  /** Metadata for the update of the item modifer. */
  itemMetadata?: ItemMetadata;
  /** Updated item modifier. */
  modifier?: Modifier;
}

export interface DeleteModifierRequest {
  /**
   * ID of the item modifier to delete.
   * @format GUID
   */
  modifierId: string;
}

export interface DeleteModifierResponse {}

export interface QueryModifiersRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   * See [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language)
   * for more information.
   */
  filter?: Record<string, any> | null;
  /** Sort object. */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface QueryModifiersResponse {
  /** Retrieved item modifiers. */
  modifiers?: Modifier[];
  /** Metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CountModifiersRequest {
  /** Filter for counting modifiers. */
  filter?: Record<string, any> | null;
}

export interface CountModifiersResponse {
  /** Counted modifiers. */
  count?: number;
}

export interface BulkDeleteModifiersRequest {
  /**
   * Item Modifier IDs.
   * @format GUID
   * @maxSize 200
   */
  ids: string[];
}

export interface BulkDeleteModifiersResponse {
  /** Information about the deleted modifiers. */
  results?: BulkDeleteModifiersResult[];
  /** Metadata for the API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteModifiersResult {
  /** Metadata for modifiers deletion. */
  itemMetadata?: ItemMetadata;
}

export interface CloneModifiersRequest {
  /**
   * The MetaSiteId to clone from
   * @format GUID
   */
  metaSiteId?: string;
}

export interface CloneModifiersResponse {}

export interface BulkUpdateBusinessLocationIdsRequest {
  /**
   * modifier ids
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  modifierIds?: string[];
  /**
   * Business Location Ids.
   * @maxSize 20
   * @format GUID
   */
  businessLocationIds?: string[];
}

export interface BulkUpdateBusinessLocationIdsResponse {
  /**
   * Information about the updated modifiers.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateBusinessLocationIdsResponseBulkItemResult[];
}

export interface BulkUpdateBusinessLocationIdsResponseBulkItemResult {
  /** Metadata for item update. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateBusinessLocationIdsPerEntityRequest {
  /**
   * @minSize 1
   * @maxSize 100
   */
  modifierIdLocationIds?: ModifierIdLocationIds[];
}

export interface ModifierIdLocationIds {
  /**
   * modifier ID
   * @format GUID
   */
  modifierId?: string;
  /**
   * Business Location IDs
   * @maxSize 20
   * @format GUID
   */
  businessLocationIds?: string[];
}

export interface BulkUpdateBusinessLocationIdsPerEntityResponse {
  /**
   * Information about the updated modifiers.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateBusinessLocationIdsPerEntityResponseBulkItemResult[];
}

export interface BulkUpdateBusinessLocationIdsPerEntityResponseBulkItemResult {
  /** Metadata for item update. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateAttachedToOrphanItemRequest {
  /**
   * @minSize 1
   * @maxSize 100
   */
  modifierIdAttachedToOrphanItem?: ModifierIdAttachedToOrphanItem[];
}

export interface ModifierIdAttachedToOrphanItem {
  /**
   * Modifier ID
   * @format GUID
   */
  modifierId?: string;
  /** Attached to orphan item */
  attachedToOrphanItem?: boolean;
}

export interface BulkUpdateAttachedToOrphanItemResponse {
  /**
   * Information about the updated modifiers.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkItemResult[];
}

export interface BulkItemResult {
  /** Metadata for item update. */
  itemMetadata?: ItemMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface SyncContentToMultilingualRequest {
  /**
   * MetaSite ID
   * @format GUID
   */
  metaSiteId?: string;
}

export interface SyncContentToMultilingualResponse {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CountModifiersApplicationErrors = {
  code?: 'TOO_MANY_TO_COUNT';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ItemModifierCreatedEnvelope {
  entity: Modifier;
  metadata: EventMetadata;
}

/** @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.MODIFIER_READ
 * @webhook
 * @eventType wix.restaurants.menus.v1.item_modifier_created
 * @serviceIdentifier wix.restaurants.menus_item_modifier.v1.ModifiersService
 * @slug created
 * @documentationMaturity preview
 */
export declare function onItemModifierCreated(
  handler: (event: ItemModifierCreatedEnvelope) => void | Promise<void>
): void;

export interface ItemModifierDeletedEnvelope {
  metadata: EventMetadata;
}

/** @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.MODIFIER_READ
 * @webhook
 * @eventType wix.restaurants.menus.v1.item_modifier_deleted
 * @serviceIdentifier wix.restaurants.menus_item_modifier.v1.ModifiersService
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onItemModifierDeleted(
  handler: (event: ItemModifierDeletedEnvelope) => void | Promise<void>
): void;

export interface ItemModifierUpdatedEnvelope {
  entity: Modifier;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/** @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.MODIFIER_READ
 * @webhook
 * @eventType wix.restaurants.menus.v1.item_modifier_updated
 * @serviceIdentifier wix.restaurants.menus_item_modifier.v1.ModifiersService
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onItemModifierUpdated(
  handler: (event: ItemModifierUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * > **Note:** The Item Modifiers API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Creates an item modifier.
 *
 * To create multiple item modifiers at once, use [Bulk Create Modifiers](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item-modifiers/bulk-create-modifiers).
 * @param modifier - Item modifier to create.
 * @public
 * @documentationMaturity preview
 * @requiredField modifier
 * @requiredField modifier.name
 * @permissionId RESTAURANTS.MODIFIER_CREATE
 * @applicableIdentity APP
 * @returns Created item modifier.
 * @fqn wix.restaurants.menus_item_modifier.v1.ModifiersService.CreateModifier
 */
export async function createModifier(
  modifier: NonNullablePaths<Modifier, `name`, 2>
): Promise<NonNullablePaths<Modifier, `businessLocationIds`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ modifier: modifier });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1ItemModifier.createModifier(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.modifier!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { modifier: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['modifier']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves an item modifier by ID.
 * @param modifierId - ID of the item modifier to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField modifierId
 * @permissionId RESTAURANTS.MODIFIER_READ
 * @applicableIdentity APP
 * @returns Retrieved item modifier.
 * @fqn wix.restaurants.menus_item_modifier.v1.ModifiersService.GetModifier
 */
export async function getModifier(
  modifierId: string
): Promise<NonNullablePaths<Modifier, `businessLocationIds`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    modifierId: modifierId,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1ItemModifier.getModifier(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.modifier!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { modifierId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['modifierId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves a list of up to 500 item modifiers.
 * @public
 * @documentationMaturity preview
 * @permissionId RESTAURANTS.MODIFIER_READ
 * @applicableIdentity APP
 * @fqn wix.restaurants.menus_item_modifier.v1.ModifiersService.ListModifiers
 */
export async function listModifiers(
  options?: ListModifiersOptions
): Promise<NonNullablePaths<ListModifiersResponse, `modifiers`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    modifierIds: options?.modifierIds,
    paging: options?.paging,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1ItemModifier.listModifiers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          modifierIds: '$[0].modifierIds',
          paging: '$[0].paging',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListModifiersOptions {
  /**
   * IDs of the item modifiers to retrieve.
   * @format GUID
   * @maxSize 500
   */
  modifierIds?: string[];
  /** Metadata of the paginated results. */
  paging?: CursorPaging;
}

/**
 * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates an item modifier.
 *
 * To update multiple item modifiers at once, use [Bulk Update Item Modifiers](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item-modifiers/bulk-update-modifiers).
 *
 * Each time an item modifier is updated, its revision increments by 1. The existing revision must be included when updating an item modifier. This ensures you're working with the latest item modifier information, and it prevents unintended overwrites.
 * @param _id - Item modifier ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField modifier
 * @requiredField modifier.revision
 * @permissionId RESTAURANTS.MODIFIER_UPDATE
 * @applicableIdentity APP
 * @returns Updated item modifier.
 * @fqn wix.restaurants.menus_item_modifier.v1.ModifiersService.UpdateModifier
 */
export async function updateModifier(
  _id: string,
  modifier: NonNullablePaths<UpdateModifier, `revision`, 2>
): Promise<NonNullablePaths<Modifier, `businessLocationIds`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    modifier: { ...modifier, id: _id },
  });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1ItemModifier.updateModifier(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.modifier!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { modifier: '$[1]' },
        explicitPathsToArguments: { 'modifier.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'modifier']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateModifier {
  /**
   * Item modifier ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the item modifier is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the item modifier. <br />
   *
   * Ignored when creating an item modifier.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the item modifier was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the item modifier was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Item modifier display name.
   * @minLength 1
   * @maxLength 200
   */
  name?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /**
   * Whether the modifier is in stock.
   * Default: `true`.
   */
  inStock?: boolean | null;
  /**
   * IDs of the business locations ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where these item modifiers are available.
   * @maxSize 100
   * @format GUID
   * @readonly
   */
  businessLocationIds?: string[];
}

/**
 * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Creates multiple item modifiers.
 * @param modifiers - List of item modifiers to create.
 * @public
 * @documentationMaturity preview
 * @requiredField modifiers
 * @permissionId RESTAURANTS.MODIFIER_CREATE
 * @applicableIdentity APP
 * @fqn wix.restaurants.menus_item_modifier.v1.ModifiersService.BulkCreateModifiers
 */
export async function bulkCreateModifiers(
  modifiers: Modifier[],
  options?: BulkCreateModifiersOptions
): Promise<
  NonNullablePaths<
    BulkCreateModifiersResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    modifiers: modifiers,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1ItemModifier.bulkCreateModifiers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          modifiers: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['modifiers', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateModifiersOptions {
  /**
   * Whether the created item modifiers are included in the response. <br />
   * Default: `false`.
   */
  returnEntity?: boolean;
}

/**
 * > **Note:** The Item modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates multiple item modifiers at once.
 * Each time an item modifier is updated, its revision increments by 1. The existing revision must be included when updating the item modifier. This ensures you're working with the latest item modifier information, and prevents unintended overwrites.
 * @param modifiers - List of item modifiers to update.
 * @public
 * @documentationMaturity preview
 * @requiredField modifiers
 * @permissionId RESTAURANTS.MODIFIER_UPDATE
 * @applicableIdentity APP
 * @fqn wix.restaurants.menus_item_modifier.v1.ModifiersService.BulkUpdateModifiers
 */
export async function bulkUpdateModifiers(
  modifiers: MaskedModifier[],
  options?: BulkUpdateModifiersOptions
): Promise<
  NonNullablePaths<
    BulkUpdateModifiersResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    modifiers: modifiers,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1ItemModifier.bulkUpdateModifiers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          modifiers: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['modifiers', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateModifiersOptions {
  /** Whether the updated item modifiers are included in the response. */
  returnEntity?: boolean;
}

/**
 * > **Note:** The Item modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 * Deletes an item modifier.
 * @param modifierId - ID of the item modifier to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField modifierId
 * @permissionId RESTAURANTS.MODIFIER_DELETE
 * @applicableIdentity APP
 * @fqn wix.restaurants.menus_item_modifier.v1.ModifiersService.DeleteModifier
 */
export async function deleteModifier(modifierId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    modifierId: modifierId,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1ItemModifier.deleteModifier(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { modifierId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['modifierId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of item modifiers.
 *
 * The `queryModifiers()` function builds a query to retrieve a list of item modifiers and returns a `ModifiersQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/item-modifiers/modifiers-query-builder/find) function.
 *
 * You can refine the query by chaining `ModifiersQueryBuilder` functions onto the query. `ModifiersQueryBuilder` functions enable you to filter, sort, and control the results that `queryModifiers()` returns.
 *
 * `queryModifiers()` runs with the following `ModifiersQueryBuilder` defaults, which you can override:
 *
 * * [`limit(500)`](/item-modifiers/modifiers-query-builder/limit)
 *
 * The following `ModifiersQueryBuilder` functions are supported for `queryModifiers()`. For a full description of the item modifier object, see the object returned for the [`items`](/item-modifiers/modifiers-query-result/items) property in `ModifiersQueryResult`.
 * @public
 * @documentationMaturity preview
 * @permissionId RESTAURANTS.MODIFIER_READ
 * @applicableIdentity APP
 * @fqn wix.restaurants.menus_item_modifier.v1.ModifiersService.QueryModifiers
 */
export function queryModifiers(): ModifiersQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Modifier,
    'CURSOR',
    QueryModifiersRequest,
    QueryModifiersResponse
  >({
    func: async (payload: QueryModifiersRequest) => {
      const reqOpts =
        ambassadorWixRestaurantsMenusV1ItemModifier.queryModifiers(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryModifiersRequest['query']) => {
      const args = [query, {}] as [QueryModifiersRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryModifiersResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.modifiers,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface ModifiersQueryResult extends QueryCursorResult {
  items: Modifier[];
  query: ModifiersQueryBuilder;
  next: () => Promise<ModifiersQueryResult>;
  prev: () => Promise<ModifiersQueryResult>;
}

export interface ModifiersQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => ModifiersQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => ModifiersQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => ModifiersQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => ModifiersQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => ModifiersQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => ModifiersQueryBuilder;
  /** @documentationMaturity preview */
  in: (propertyName: '_id' | 'name', value: any) => ModifiersQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => ModifiersQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => ModifiersQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<ModifiersQueryResult>;
}

/**
 * @hidden
 * @fqn wix.restaurants.menus_item_modifier.v1.ModifiersService.QueryModifiers
 * @requiredField query
 */
export async function typedQueryModifiers(
  query: ModifierQuery
): Promise<NonNullablePaths<QueryModifiersResponse, `modifiers`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1ItemModifier.queryModifiers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ModifierQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_id', 'name'];
      operators: ['$eq', '$in', '$ne', '$nin'];
      sort: 'NONE';
    },
    {
      fields: ['_createdDate', '_updatedDate'];
      operators: ['$eq', '$gt', '$gte', '$lt', '$lte', '$ne'];
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  Modifier,
  ModifierQuerySpec
>;
export type ModifierQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to load. 
  @max: 500 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request.  
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.
  See [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language)
  for more information.  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Modifier, ModifierQuerySpec, ModifierQuery>(),
  },
};

/**
 * > **Note:** The Item API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves the number of modifiers that match a specified filter.
 *
 * If a filter isn't passed in the request, the endpoint returns the count of all modifiers.
 * @public
 * @documentationMaturity preview
 * @permissionId RESTAURANTS.MODIFIER_READ
 * @applicableIdentity APP
 * @fqn wix.restaurants.menus_item_modifier.v1.ModifiersService.CountModifiers
 */
export async function countModifiers(options?: CountModifiersOptions): Promise<
  NonNullablePaths<CountModifiersResponse, `count`, 2> & {
    __applicationErrorsType?: CountModifiersApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1ItemModifier.countModifiers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0].filter' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CountModifiersOptions {
  /** Filter for counting modifiers. */
  filter?: Record<string, any> | null;
}

/**
 * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Deletes multiple item Modifiers at once.
 * @param ids - Item Modifier IDs.
 * @public
 * @documentationMaturity preview
 * @requiredField ids
 * @permissionId RESTAURANTS.MODIFIER_DELETE
 * @applicableIdentity APP
 * @fqn wix.restaurants.menus_item_modifier.v1.ModifiersService.BulkDeleteModifiers
 */
export async function bulkDeleteModifiers(
  ids: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteModifiersResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ ids: ids });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1ItemModifier.bulkDeleteModifiers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { ids: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['ids']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
